import { Box, Button, CircularProgress, Sheet, Typography } from '@mui/joy';
import { File as FileIcon, LinkSimple, Play } from '@phosphor-icons/react';
import { Fancybox } from '@fancyapps/ui';
import '@fancyapps/ui/dist/fancybox/fancybox.css';
import { useCallback } from 'react';
import FormatDateDistanceToNow from '../FormatDateDistanceToNow/FormatDateDistanceToNow';
import { useChatContent } from '../../../hooks/messages/useChatContent';
import { mediaUrl } from '../../../utils/mediaUrl';

const fileUrl = (url) => mediaUrl(url) ?? '';

/** Размер в человекочитаемом виде: 0 и null одинаково значат «неизвестно». */
function formatSize(bytes) {
    if (!bytes || bytes <= 0) return null;
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

/**
 * Убирает схему и www, чтобы в списке читался домен, а не «https://».
 * Показывать ссылку целиком незачем: в узкой строке она всё равно обрежется
 * ровно на той части, которая ни о чём не говорит.
 */
function prettyUrl(url) {
    return String(url).replace(/^https?:\/\//i, '').replace(/^www\./i, '');
}

const rowSx = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2,
    py: 1.25,
    mb: 0.5,
    borderRadius: '16px',
    cursor: 'pointer',
    textDecoration: 'none !important',
    color: 'inherit',
    '&:hover': { backgroundColor: 'neutral.softHoverBg' },
};

const iconSx = {
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'background.level1',
};

function MediaGrid({ items }) {
    // Просмотрщику отдаём ВЕСЬ загруженный список, а не одну картинку: иначе
    // внутри него нельзя было бы листать к соседним снимкам.
    const openViewer = useCallback((index) => {
        Fancybox.show(
            items.map(item => ({
                src: fileUrl(item.url),
                type: item.type === 'video' ? 'video' : 'image',
            })),
            {
                startIndex: index,
                animated: false,
                dragToClose: true,
                closeButton: 'top',
                Thumbs: false,
                Toolbar: { display: ['close', 'zoom'] },
            }
        );
    }, [items]);

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '3px',
            }}
        >
            {items.map((item, index) => (
                <Box
                    key={`${item.messageId}-${item.url}`}
                    onClick={() => openViewer(index)}
                    sx={{
                        position: 'relative',
                        aspectRatio: '1 / 1',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        cursor: 'pointer',
                        backgroundColor: 'background.level1',
                    }}
                >
                    {item.type === 'video' ? (
                        <Box
                            component="video"
                            src={fileUrl(item.url)}
                            preload="metadata"
                            muted
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    ) : (
                        <Box
                            component="img"
                            src={fileUrl(item.url)}
                            alt=""
                            loading="lazy"
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                        />
                    )}

                    {/* У видео превью — тот же кадр, поэтому без значка его не отличить. */}
                    {item.type === 'video' && (
                        <Box
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 32,
                                height: 32,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'rgba(0,0,0,0.55)',
                            }}
                        >
                            <Play size={16} weight="fill" color="#fff" />
                        </Box>
                    )}
                </Box>
            ))}
        </Box>
    );
}

function FilesList({ items }) {
    return (
        <Box>
            {items.map(item => {
                const name = item.name ?? String(item.url).split('/').pop() ?? 'Файл';
                const size = formatSize(item.size);

                return (
                    <Sheet
                        key={`${item.messageId}-${item.url}`}
                        component="a"
                        href={fileUrl(item.url)}
                        download={name}
                        target="_blank"
                        rel="noreferrer"
                        variant="plain"
                        sx={rowSx}
                    >
                        <Box sx={iconSx}>
                            <FileIcon size={18} color="var(--joy-palette-primary-500)" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography level="title-sm" noWrap>{name}</Typography>
                            <Typography level="body-xs" textColor="text.tertiary">
                                {size ? `${size} · ` : ''}
                                <FormatDateDistanceToNow dateString={item.created_at} />
                            </Typography>
                        </Box>
                    </Sheet>
                );
            })}
        </Box>
    );
}

function LinksList({ items }) {
    return (
        <Box>
            {items.map(item => {
                // Подпись — текст сообщения без самой ссылки: дублировать её
                // строкой ниже бессмысленно, а оставшийся комментарий помогает
                // вспомнить контекст.
                const caption = String(item.text ?? '').replace(item.url, '').trim();

                return (
                    <Sheet
                        key={`${item.messageId}-${item.url}`}
                        component="a"
                        href={item.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        variant="plain"
                        sx={{ ...rowSx, alignItems: 'flex-start' }}
                    >
                        <Box sx={iconSx}>
                            <LinkSimple size={18} color="var(--joy-palette-primary-500)" />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography level="title-sm" noWrap>{prettyUrl(item.url)}</Typography>
                            {caption.length > 0 && (
                                <Typography
                                    level="body-sm"
                                    textColor="text.tertiary"
                                    sx={{
                                        display: '-webkit-box',
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {caption}
                                </Typography>
                            )}
                            <Typography level="body-xs" textColor="text.tertiary">
                                <FormatDateDistanceToNow dateString={item.created_at} />
                            </Typography>
                        </Box>
                    </Sheet>
                );
            })}
        </Box>
    );
}

/**
 * Разделы «Медиа / Файлы / Ссылки» в профиле собеседника.
 *
 * Показывают содержимое ПЕРЕПИСКИ с этим человеком — то же, что и одноимённые
 * экраны в приложении.
 *
 * @param {{userId?: number}|{groupChatId?: number}} source - чья переписка
 * @param {'media'|'files'|'links'} type
 */
export default function ProfileChatContent({ source, type }) {
    const { items, loading, loadingMore, hasMore, loadMore, error } = useChatContent(source, type);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography level="body-sm" color="danger" sx={{ py: 3, textAlign: 'center' }}>
                {error}
            </Typography>
        );
    }

    if (items.length === 0) {
        return (
            <Typography level="body-sm" textColor="text.tertiary" sx={{ py: 3, textAlign: 'center' }}>
                Здесь пока пусто.
            </Typography>
        );
    }

    return (
        <Box sx={{ mt: 2 }}>
            {type === 'media' && <MediaGrid items={items} />}
            {type === 'files' && <FilesList items={items} />}
            {type === 'links' && <LinksList items={items} />}

            {hasMore && (
                <Button
                    variant="soft"
                    color="neutral"
                    fullWidth
                    loading={loadingMore}
                    onClick={loadMore}
                    sx={{ mt: 2, borderRadius: '999px', py: 1.25 }}
                >
                    Показать ещё
                </Button>
            )}
        </Box>
    );
}
